import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Storage as StorageIcon,
  CloudQueue as CloudQueueIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  CloudDone as CloudDoneIcon,
  Verified as VerifiedIcon,
  Accessibility as AccessibilityIcon,
  Architecture as ArchitectureIcon,
  LockOpen as LockOpenIcon
} from '@mui/icons-material';

const ScalableArchitecture: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(4);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };

  const benefits = [
    'Handle millions of data assets across hundreds of systems',
    'Horizontal scaling supports unlimited growth',
    'Microservices architecture enables independent scaling',
    'Load balancing ensures optimal resource utilization',
    'Auto-scaling responds to demand automatically',
    'No performance degradation as data volume increases'
  ];

  const capabilities = [
    {
      icon: <ArchitectureIcon sx={{ color: 'primary.main' }} />,
      title: 'Microservices Design',
      description: 'Loosely coupled services scale independently based on specific needs'
    },
    {
      icon: <CloudQueueIcon sx={{ color: 'primary.main' }} />,
      title: 'Cloud-Native',
      description: 'Built for cloud environments with containerization and orchestration'
    },
    {
      icon: <StorageIcon sx={{ color: 'primary.main' }} />,
      title: 'Distributed Data',
      description: 'Sharded databases and distributed caching for massive scale'
    },
    {
      icon: <SpeedIcon sx={{ color: 'primary.main' }} />,
      title: 'Load Balancing',
      description: 'Intelligent traffic distribution across multiple instances'
    }
  ];

  const useCases = [
    {
      title: 'Enterprise-Wide Deployment',
      description: 'Support thousands of users across multiple agencies with consistent performance'
    },
    {
      title: 'Data Growth Management',
      description: 'Seamlessly accommodate exponential data growth without infrastructure overhaul'
    },
    {
      title: 'Peak Load Handling',
      description: 'Automatically scale resources during high-demand periods like reporting cycles'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back to About Page
      </Button>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: 'primary.main',
            minHeight: 64,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: 'primary.main',
            height: 3
          }
        }}
      >
        <Tab label="High Performance" icon={<SpeedIcon />} iconPosition="start" />
        <Tab label="Zero Trust" icon={<ShieldIcon />} iconPosition="start" />
        <Tab label="FedRAMP Ready" icon={<CloudDoneIcon />} iconPosition="start" />
        <Tab label="508 Compliant" icon={<AccessibilityIcon />} iconPosition="start" />
        <Tab label="Scalable" icon={<TrendingUpIcon />} iconPosition="start" />
        <Tab label="No Lock-In" icon={<LockOpenIcon />} iconPosition="start" />
      </Tabs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TrendingUpIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Scalable Architecture
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Handles millions of data assets across hundreds of systems
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography paragraph sx={{ fontSize: '0.95rem' }}>
              E-Unify's scalable architecture is designed to grow with your organization, from small pilot programs to enterprise-wide deployments managing millions of data assets. Our cloud-native, microservices-based design ensures that the platform can scale horizontally to meet any demand without compromising performance.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              By leveraging modern architectural patterns including containerization, orchestration, and distributed computing, E-Unify provides unlimited scalability while maintaining cost efficiency. Resources scale automatically based on demand, ensuring optimal performance during both normal operations and peak usage periods.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Architecture Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
        Common Use Cases
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {useCases.map((useCase, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 3, borderLeft: '4px solid #003366' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {useCase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {useCase.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: 'primary.main',
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Scale Without Limits
        </Typography>
        <Typography variant="body1" paragraph>
          Discover how E-Unify's architecture grows with your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'primary.main',
            '&:hover': {
              bgcolor: '#f0f0f0'
            }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default ScalableArchitecture;
